/**
 * Client-side service for managing the slot-core Vite dev server
 * via the ai-proxy backend at /api/slot-core.
 */

export interface DevServerStatus {
  running: boolean;
  pid: number | null;
  port: number;
  startedAt: string | null;
  error: string | null;
}

const BASE_URL = '/api/slot-core';

export class SlotCoreDevServerService {
  /**
   * Get the current status of the dev server.
   */
  static async getStatus(): Promise<DevServerStatus> {
    try {
      const res = await fetch(`${BASE_URL}/status`);
      if (!res.ok) throw new Error(`Status check failed: ${res.status}`);
      return res.json();
    } catch {
      return { running: false, pid: null, port: 8080, startedAt: null, error: 'Backend not reachable' };
    }
  }

  /**
   * Start the slot-core dev server.
   * @param projectPath - Absolute path to the user's project directory
   */
  static async start(projectPath: string): Promise<DevServerStatus> {
    try {
      const res = await fetch(`${BASE_URL}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectPath }),
      });
      if (!res.ok) throw new Error(`Start failed: ${res.status}`);
      return res.json();
    } catch (err) {
      return {
        running: false,
        pid: null,
        port: 8080,
        startedAt: null,
        error: err instanceof Error ? err.message : 'Start failed',
      };
    }
  }

  /**
   * Stop the slot-core dev server.
   */
  static async stop(): Promise<DevServerStatus> {
    try {
      const res = await fetch(`${BASE_URL}/stop`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`Stop failed: ${res.status}`);
      return res.json();
    } catch (err) {
      return {
        running: false,
        pid: null,
        port: 8080,
        startedAt: null,
        error: err instanceof Error ? err.message : 'Stop failed',
      };
    }
  }
}
